import { Component, Inject, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { HomeCarouselImage, HomeCarouselImageService, PaginatedResponse } from '../home-carousel-image.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize, tap } from 'rxjs/operators';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Observable, forkJoin } from 'rxjs';

@Component({
  selector: 'app-carousel-config-dialog',
  templateUrl: './carousel-config-dialog.component.html',
  styleUrls: ['./carousel-config-dialog.component.sass']
})
export class CarouselConfigDialogComponent implements OnInit, AfterViewInit {
  @ViewChild('fileInput') fileInput: ElementRef;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  
  carouselImages: HomeCarouselImage[] = [];
  dataSource = new MatTableDataSource<HomeCarouselImage>([]);
  displayedColumns: string[] = ['order', 'image', 'title', 'isActive', 'actions'];
  
  carouselForm: FormGroup;
  isEditing = false;
  currentImageId: string | null = null;
  loading = false;
  selectedFiles: File[] = [];
  imagePreview: string | null = null;
  uploadProgress = 0;
  isUploading = false;
  currentFileIndex = 0;

  // Propriedades de paginação
  totalItems = 0;
  pageSize = 6;
  currentPage = 0;
  paginatorInitialized = false;

  constructor(
    public dialogRef: MatDialogRef<CarouselConfigDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private carouselService: HomeCarouselImageService,
    private snackBar: MatSnackBar
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.loadCarouselImages();
  }

  ngAfterViewInit(): void {
    // Verificar se o paginador foi inicializado corretamente antes de inscrever-se em eventos
    setTimeout(() => {
      if (this.paginator) {
        this.paginatorInitialized = true;
        this.paginator.page.subscribe((event: PageEvent) => {
          this.currentPage = event.pageIndex;
          this.pageSize = event.pageSize;
          this.loadCarouselImages();
        });
      }
    });
  }

  initForm(): void {
    this.carouselForm = this.fb.group({
      title: ['', [Validators.required]],
      description: [''],
      imageUrl: [''],
      link: [''],
      order: [1, [Validators.required, Validators.min(1), Validators.max(15)]],
      isActive: [true]
    });
  }

  loadCarouselImages(): void {
    this.loading = true;
    
    console.log(`Carregando imagens - Página: ${this.currentPage}, Limite: ${this.pageSize}`);
    
    this.carouselService.getPaginated(this.currentPage, this.pageSize)
      .pipe(
        finalize(() => this.loading = false),
        tap(() => {
          // Notificar o componente pai que as imagens foram atualizadas
          this.dialogRef.componentInstance.data = { 
            ...this.dialogRef.componentInstance.data,
            refreshImages: true 
          };
        })
      )
      .subscribe(
        (response: PaginatedResponse<HomeCarouselImage>) => {
          console.log('Imagens carregadas:', response);
          this.carouselImages = response.images || [];
          this.totalItems = response.total || 0;
          this.formatImageUrls();
          this.dataSource.data = this.carouselImages;
          
          // Atualizar o paginador apenas se o total ou a configuração mudou
          if (this.paginator) {
            this.paginator.length = this.totalItems;
            this.paginator.pageIndex = this.currentPage;
          }
        },
        (error: any) => {
          console.error('Erro ao carregar imagens:', error);
          this.handleError('Erro ao carregar imagens do carrossel', error);
        }
      );
  }

  onPageChange(event: PageEvent): void {
    console.log('Mudança de página:', event);
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadCarouselImages();
  }

  formatImageUrls(): void {
    this.carouselImages.forEach(image => {
      image.imageUrl = this.carouselService.getFormattedImageUrl(image.imageUrl);
    });
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFiles = Array.from(input.files);
      this.createImagePreview();
    }
  }

  createImagePreview(): void {
    if (!this.selectedFiles.length) return;
    
    const reader = new FileReader();
    reader.onload = () => {
      this.imagePreview = reader.result as string;
    };
    reader.readAsDataURL(this.selectedFiles[0]);
  }

  saveImage(): void {
    if (this.carouselForm.invalid) {
      this.markFormGroupTouched(this.carouselForm);
      return;
    }

    if (this.isEditing) {
      this.checkOrderAvailability();
    } else if (this.selectedFiles.length > 1) {
      this.processMultipleImages();
    } else {
      this.checkOrderAvailability();
    }
  }

  processMultipleImages(): void {
    this.isUploading = true;
    let startOrder = this.carouselForm.value.order;
    const uploadObservables: Observable<any>[] = [];
    
    // Para cada arquivo, criar um FormData e adicionar à lista de observáveis
    this.selectedFiles.forEach((file, index) => {
      const orderValue = startOrder + index;
      if (orderValue <= 15) {
        const formData = this.carouselService.prepareFormData(
          {
            ...this.carouselForm.value,
            order: orderValue,
            title: index === 0 ? this.carouselForm.value.title : `${this.carouselForm.value.title} (${index + 1})`
          }, 
          file
        );
        uploadObservables.push(this.carouselService.uploadImage(formData));
      }
    });
    
    // Executar todos os uploads em paralelo
    forkJoin(uploadObservables)
      .pipe(finalize(() => this.isUploading = false))
      .subscribe(
        () => {
          this.showMessage(`${uploadObservables.length} imagens adicionadas com sucesso`, 'success');
          this.resetForm();
          // Voltar para a primeira página após adicionar novas imagens
          this.currentPage = 0;
          if (this.paginator) {
            this.paginator.firstPage();
          }
          this.loadCarouselImages();
          this.notifyParent();
        },
        (error: any) => {
          this.handleError('Erro ao adicionar imagens', error);
        }
      );
  }

  checkOrderAvailability(): void {
    const orderValue = this.carouselForm.value.order;
    
    this.carouselService.getByOrder(orderValue).subscribe(
      (existingImage: HomeCarouselImage) => {
        if (existingImage && (!this.isEditing || existingImage.id !== this.currentImageId)) {
          this.showMessage(`Já existe uma imagem com a ordem ${orderValue}`, 'warning');
          return;
        }
        
        this.uploadImage();
      },
      () => {
        this.uploadImage();
      }
    );
  }

  uploadImage(): void {
    this.isUploading = true;
    
    const formData = this.carouselService.prepareFormData(
      this.carouselForm.value, 
      this.selectedFiles.length > 0 ? this.selectedFiles[0] : undefined
    );
    
    if (this.isEditing && this.currentImageId) {
      this.updateExistingImage(formData);
    } else {
      this.createNewImage(formData);
    }
  }

  updateExistingImage(formData: FormData): void {
    if (!this.currentImageId) return;
    
    this.carouselService.updateWithFile(this.currentImageId, formData)
      .pipe(finalize(() => this.isUploading = false))
      .subscribe(
        () => {
          this.showMessage('Imagem atualizada com sucesso', 'success');
          this.resetForm();
          this.loadCarouselImages();
          this.notifyParent();
        },
        (error: any) => {
          this.handleError('Erro ao atualizar imagem', error);
        }
      );
  }

  createNewImage(formData: FormData): void {
    this.carouselService.uploadImage(formData)
      .pipe(finalize(() => this.isUploading = false))
      .subscribe(
        () => {
          this.showMessage('Imagem adicionada com sucesso', 'success');
          this.resetForm();
          this.loadCarouselImages();
          this.notifyParent();
        },
        (error: any) => {
          this.handleError('Erro ao adicionar imagem', error);
        }
      );
  }

  notifyParent(): void {
    // Notificar o componente pai para atualizar as imagens do carrossel
    this.dialogRef.componentInstance.data = {
      ...this.dialogRef.componentInstance.data,
      refreshCarousel: true
    };
  }

  editImage(image: HomeCarouselImage): void {
    this.isEditing = true;
    this.currentImageId = image.id || null;
    this.carouselForm.patchValue(image);
    this.imagePreview = image.imageUrl;
    this.selectedFiles = [];
  }

  deleteImage(image: HomeCarouselImage): void {
    if (!confirm(`Tem certeza que deseja excluir a imagem "${image.title}"?`)) {
      return;
    }
    
    if (image.id) {
      this.loading = true;
      this.carouselService.delete(image.id)
        .pipe(finalize(() => this.loading = false))
        .subscribe(
          () => {
            this.showMessage('Imagem excluída com sucesso', 'success');
            // Verificar se devemos voltar para a página anterior após exclusão
            if (this.carouselImages.length === 1 && this.currentPage > 0) {
              this.currentPage--;
            }
            this.loadCarouselImages();
            this.notifyParent();
          },
          (error: any) => {
            this.handleError('Erro ao excluir imagem', error);
          }
        );
    }
  }

  resetForm(): void {
    this.carouselForm.reset({
      title: '',
      description: '',
      imageUrl: '',
      link: '',
      order: this.findNextAvailableOrder(),
      isActive: true
    });
    this.isEditing = false;
    this.currentImageId = null;
    this.selectedFiles = [];
    this.imagePreview = null;
  }

  findNextAvailableOrder(): number {
    if (this.carouselImages.length === 0) return 1;
    
    const maxOrder = Math.max(...this.carouselImages.map(img => img.order));
    return maxOrder + 1 <= 15 ? maxOrder + 1 : 15;
  }

  close(): void {
    // Envia um sinalizador para atualizar o carrossel ao fechar
    this.dialogRef.close({ refreshCarousel: true });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private showMessage(message: string, type: 'success' | 'error' | 'warning'): void {
    this.snackBar.open(message, 'Fechar', {
      duration: 3000,
      panelClass: [`${type}-snackbar`]
    });
  }

  private handleError(message: string, error: any): void {
    console.error(`${message}:`, error);
    this.showMessage(message, 'error');
  }

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }
} 